import {
  ServiceEventTypeServiceType,
  useCreateServiceEventTypeServiceTypeMutation,
  useDeleteServiceEventTypeServiceTypeMutation,
  useUpdateServiceEventTypeServiceTypeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceEventTypeServiceTypeMutationProps = {}

export type UseServiceEventTypeServiceTypeMutationResult = {
  createServiceEventTypeServiceType: (assoc: Partial<ServiceEventTypeServiceType>, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => void
  updateServiceEventTypeServiceType: (assoc: Partial<ServiceEventTypeServiceType>, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => void
  deleteServiceEventTypeServiceType: (id: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceEventTypeServiceTypeMutation = (props: UseServiceEventTypeServiceTypeMutationProps = {}): UseServiceEventTypeServiceTypeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceEventTypeServiceType, {isLoading: isCreating}] = useCreateServiceEventTypeServiceTypeMutation()
  const [_updateServiceEventTypeServiceType, {isLoading: isUpdating}] = useUpdateServiceEventTypeServiceTypeMutation()
  const [_deleteServiceEventTypeServiceType, {isLoading: isDeleting}] = useDeleteServiceEventTypeServiceTypeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceEventTypeServiceTypeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTypeServiceTypeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceEventTypeServiceTypeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTypeServiceTypeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceEventTypeServiceTypeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceEventTypeServiceTypeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceEventTypeServiceType>, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceEVentId: string, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => {
    const promise = _deleteServiceEventTypeServiceType({id: serviceEVentId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceEventTypeServiceType, handleDeleteResult])

  const createServiceEventTypeServiceType = useCallback((serviceEVent: Partial<ServiceEventTypeServiceType>, callback: EntityMutationCallback<ServiceEventTypeServiceType> | undefined) => {
    const promise = _createServiceEventTypeServiceType(serviceEVent).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceEventTypeServiceType, handleCreateResult])

  const updateServiceEventTypeServiceType = useCallback((serviceEVent: Partial<ServiceEventTypeServiceType>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceEventTypeServiceType(serviceEVent).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceEventTypeServiceType, handleUpdateResult])

  const deleteServiceEventTypeServiceType = useCallback((serviceEVentId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.serviceeventtypeservicetype", "Service Event Type - Service Type"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.serviceeventtypeservicetype.confirm", "Are you sure you want to delete this service event type - service type association?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceEVentId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createServiceEventTypeServiceType: createServiceEventTypeServiceType,
    updateServiceEventTypeServiceType: updateServiceEventTypeServiceType,
    deleteServiceEventTypeServiceType: deleteServiceEventTypeServiceType,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
